/**
 * Shared weather system types (DON-601).
 *
 * Weather is server-authoritative and broadcast to all clients via OverworldRoomState.
 * Clients handle visual, audio, and gameplay responses; the server drives the state
 * machine and announces transitions.
 */
/** Colyseus room state weather field is a string matching WeatherState. */
// ── Message constants ───────────────────────────────────────────────────────
/**
 * Broadcast when the weather transitions (optional — room state change is the
 * primary mechanism; this message is an immediate signal before state propagates).
 */
export const MSG_WEATHER_CHANGE = 'weather_change';
// ── Duration constants ──────────────────────────────────────────────────────
/** Minimum time (ms) any weather state lasts before transitioning. */
export const WEATHER_MIN_DURATION_MS = {
    clear: 60_000, // 1 real minute minimum clear
    rain: 30_000, // 30 s minimum rain
    storm: 20_000, // 20 s minimum storm
};
/** Maximum time (ms) any weather state lasts before a forced transition check. */
export const WEATHER_MAX_DURATION_MS = {
    clear: 180_000, // 3 minutes max clear
    rain: 90_000, // 1.5 minutes max rain
    storm: 60_000, // 1 minute max storm
};
/**
 * Announcement lead time (ms) before the weather transition completes.
 * Used to darken the horizon + play audio cue before rain/storm arrives (§14.4).
 */
export const WEATHER_ANNOUNCE_MS = 5_000;
// ── Wind parameters per weather state ──────────────────────────────────────
/** Normalized wind intensity [0..1] for each weather state. */
export const WEATHER_WIND_INTENSITY = {
    clear: 0.20,
    rain: 0.55,
    storm: 0.95,
};
/** Wind direction in radians (from +X axis) used for each weather state. */
export const WEATHER_WIND_DIRECTION = {
    clear: Math.PI + 0.2, // mostly west, slight south
    rain: Math.PI * 1.15, // south-southwest
    storm: Math.PI * 0.9, // west-northwest
};
/**
 * Hook IDs for external systems that register weather callbacks.
 * Centralised here so server and client agree on names.
 */
export const WEATHER_HOOK = {
    /** Wet ground conducts electricity — registered by combat/elemental system. */
    WET_GROUND: 'weather:wetGround',
    /** Wind redirects arrows — registered by projectile system. */
    WIND_ARROWS: 'weather:windArrows',
    /** NPCs run inside during rain — registered by NPC behaviour system. */
    NPC_SHELTER: 'weather:npcShelter',
    /** Phantom shop appears during thunderstorm only (§5.9). */
    PHANTOM_SHOP: 'weather:phantomShop',
    /** Plants grow during rain (§14.3). */
    PLANT_GROWTH: 'weather:plantGrowth',
    /** Windmill power state (storm = powered, §14). */
    WINDMILL_POWER: 'weather:windmillPower',
};
//# sourceMappingURL=weather.js.map